// src/utils/rulePlans/iamRulePlans.js
// IAM-specific remediation plan helpers.

import {
  getAccessKeySlotLabel,
  getAwsEvidenceContext,
} from "../awsEvidenceContext";

import { normalizeText } from "../complianceFormatters";

export function isIamRule(ruleId = "") {
  const rule = String(ruleId || "").toUpperCase();
  return (
    rule.includes("IAM") ||
    rule.includes("ROLE_") ||
    rule.includes("USER_") ||
    rule.includes("GROUP_") ||
    rule.includes("ACCESS_KEY") ||
    rule.includes("PASSWORD_") ||
    rule.includes("INLINE_POLICY") ||
    rule.includes("ATTACHED_ROLE_POLICY") ||
    rule.includes("ATTACHED_USER_POLICY") ||
    rule.includes("ADMIN_POLICY") ||
    rule.includes("WILDCARD") ||
    rule.includes("ASSUME")
  );
}

function pickIdentityName(awsContext = {}, item = {}) {
  return (
    awsContext?.identityName ||
    awsContext?.roleName ||
    awsContext?.userName ||
    awsContext?.groupName ||
    item?.aws_context?.identity_name ||
    item?.aws_context?.role_name ||
    item?.aws_context?.user_name ||
    item?.resource_id ||
    item?.resourceId ||
    ""
  );
}

function pickRoleName(awsContext = {}, item = {}) {
  const raw =
    awsContext?.roleName ||
    awsContext?.identityName ||
    awsContext?.roleArn ||
    item?.aws_context?.role_name ||
    item?.aws_context?.identity_name ||
    item?.resource_id ||
    item?.resourceId ||
    "the affected role";

  return String(raw).includes("/") ? String(raw).split("/").pop() : raw;
}

function pickUserName(awsContext = {}, item = {}) {
  return (
    awsContext?.userName ||
    awsContext?.identityName ||
    item?.aws_context?.user_name ||
    item?.aws_context?.identity_name ||
    item?.resource_id ||
    item?.resourceId ||
    "the affected user"
  );
}

function pickPolicyName(awsContext = {}, item = {}, fallback = "the affected policy") {
  return (
    awsContext?.policyName ||
    awsContext?.policyArn ||
    item?.aws_context?.policy_name ||
    item?.finding_metadata?.request_parameters?.policyName ||
    fallback
  );
}

function isUnusedRoleRule(rule = "") {
  return (
    rule.includes("IAM_ROLE_UNUSED") ||
    rule.includes("ROLE_UNUSED") ||
    rule.includes("UNUSED_ROLE")
  );
}

function isUserMfaAndActiveKeyRule(rule = "") {
  return (
    rule.includes("IAM_USER_HAS_NO_MFA_AND_ACTIVE_KEY") ||
    rule.includes("NO_MFA_AND_ACTIVE_KEY") ||
    (rule.includes("IAM_USER") && rule.includes("MFA") && rule.includes("ACTIVE_KEY"))
  );
}

export function buildIamFixPlanSteps(item = {}, relatedSops = [], task = {}) {
  const rule = String(item?.rule_id || item?.ruleId || "").toUpperCase();
  const awsContext = getAwsEvidenceContext(item, task);

  if (
    rule.includes("ROLE_ASSUME_EXTERNAL_ACCOUNT") ||
    rule.includes("IAM_ROLE_TRUST_POLICY_EXTERNAL_ACCOUNT") ||
    rule.includes("TRUST_POLICY_EXTERNAL_ACCOUNT")
  ) {
    const roleName = pickRoleName(awsContext, item);

    return [
      "Open AWS IAM.",
      "Go to Roles.",
      `Search for and open ${roleName}.`,
      "Open the Trust relationships tab.",
      "Review the trusted principals allowed to call sts:AssumeRole.",
      "Identify any AWS account principals, root principals, wildcard principals, or cross-account role principals in the trust policy.",
      "Confirm whether each external AWS account or principal is approved, owned, documented, and still required.",
      "Remove any unapproved, unnecessary, unknown, or overly broad external principal from the trust policy.",
      "If external access is required, restrict the Principal to the approved AWS account or role ARN and add ExternalId, aws:SourceAccount, aws:SourceArn, or other condition controls where appropriate.",
      "Confirm the role permissions are least-privilege for the approved trust relationship and business purpose.",
      "Save before/after trust-policy evidence and document the approval or exception in OASYS.",
      "Return to OASYS and run Full Cloud Assessment to verify the external trust-policy finding is cleared or properly documented.",
    ];
  }

  if (isUnusedRoleRule(rule)) {
    const roleName = pickRoleName(awsContext, item);

    return [
      "Open AWS IAM.",
      "Go to Roles.",
      `Search for and open ${roleName}.`,
      "Review the role summary, trusted entities, attached policies, inline policies, and tags.",
      "Open Access Advisor and review service access history for the role.",
      "Review CloudTrail AssumeRole activity for recent use, including the calling principal and source account.",
      "Confirm with the business owner whether this role is still required by any workload, AWS service, integration, deployment pipeline, or automation.",
      "If the role is no longer required, detach unnecessary policies and delete the role after approval.",
      "If the role must be retained, document the business owner, purpose, last-use review, and approved exception in OASYS.",
      "Save evidence of the role activity review, owner approval, deletion, or approved exception.",
      "Return to OASYS and run Full Cloud Assessment to verify the unused-role condition is cleared or properly documented.",
    ];
  }

  if (rule.includes("INLINE_POLICY_ADDED_EVENT")) {
    const identityType = normalizeText(awsContext?.identityType);
    const identityName = pickIdentityName(awsContext, item);
    const policyName = pickPolicyName(awsContext, item, "the newly added inline policy");

    if (identityName) {
      const destination =
        identityType === "user"
          ? "Users"
          : identityType === "group"
          ? "User groups"
          : "Roles";

      return [
        "Open AWS IAM.",
        `Go to ${destination}.`,
        `Search for and open ${identityName}.`,
        "Open the Permissions tab.",
        "Expand Inline policies.",
        `Find and review ${policyName}.`,
        `Remove ${policyName} if it is not approved, or replace it with a least-privilege managed policy.`,
        "Return to OASYS and run Full Cloud Assessment to verify the inline policy posture is refreshed.",
      ];
    }

    return [
      "Open AWS IAM.",
      "Go to Users or Roles depending on the identity named in the CloudTrail evidence.",
      "Open the affected identity and select the Permissions tab.",
      "Expand Inline policies and identify the newly added inline policy.",
      "Review the policy JSON for admin, wildcard, or unnecessary permissions.",
      "Remove the inline policy if it is not approved, or replace it with a least-privilege managed policy.",
      "Document any approved exception in OASYS and link the governing SOP.",
      "Return to OASYS and run Full Cloud Assessment to verify the event is handled and the current posture is refreshed.",
    ];
  }

  if (rule.includes("ATTACHED_ROLE_POLICY_EVENT")) {
    const roleName = awsContext?.roleName || "the affected role";
    const policyName = pickPolicyName(awsContext, item, "the recently attached policy");

    return [
      "Open AWS IAM.",
      "Go to Roles.",
      `Search for and open ${roleName}.`,
      "Open the Permissions tab.",
      `Find and review ${policyName}.`,
      "Confirm whether this policy is approved for this role and whether the permissions are least-privilege.",
      `Detach ${policyName} if it was temporary, unnecessary, or broader than approved.`,
      "Return to OASYS and run Full Cloud Assessment to verify the role policy posture is refreshed.",
    ];
  }

  if (rule.includes("ATTACHED_USER_POLICY_EVENT")) {
    const userName = pickUserName(awsContext, item);
    const policyName = pickPolicyName(awsContext, item, "the recently attached policy");

    return [
      "Open AWS IAM.",
      "Go to Users.",
      `Search for and open ${userName}.`,
      "Open the Permissions tab.",
      `Find and review ${policyName}.`,
      "Confirm whether this policy is approved for this user and whether the permissions are least-privilege.",
      `Detach ${policyName} if it is unnecessary, temporary, or broader than approved.`,
      "Return to OASYS and run Full Cloud Assessment to verify the user policy posture is refreshed.",
    ];
  }

  if (rule.includes("ADMIN_POLICY_ATTACHED_EVENT")) {
    const identityType = normalizeText(awsContext?.identityType);
    const identityName = pickIdentityName(awsContext, item) || "the affected identity";
    const policyName = pickPolicyName(awsContext, item, "AdministratorAccess or the administrative policy");
    const destination =
      identityType === "user"
        ? "Users"
        : identityType === "group"
        ? "User groups"
        : identityType === "role"
        ? "Roles"
        : "Users, Roles, or User groups";

    return [
      "Open AWS IAM.",
      `Go to ${destination}.`,
      `Search for and open ${identityName}.`,
      "Open the Permissions tab.",
      `Look for ${policyName}.`,
      "Remove the admin policy unless there is a documented business-approved exception.",
      "Replace it with least-privilege permissions scoped to the required service/action/resource.",
      "Run Full Cloud Assessment to confirm the admin-policy condition has been resolved or recorded.",
    ];
  }

  if (rule.includes("IAM_ROOT_MFA")) {
    return [
      "Sign in to the AWS account as the root user.",
      "Open Account settings or Security credentials for the root user.",
      "Find Multi-factor authentication (MFA).",
      "Choose Assign MFA device.",
      "Register a hardware key, virtual authenticator app, or approved MFA method.",
      "Confirm MFA shows as active for the root user.",
      "Sign out and store recovery details according to the governing access-control SOP.",
      "Return to OASYS and run Full Cloud Assessment to verify root MFA is detected.",
    ];
  }

  if (isUserMfaAndActiveKeyRule(rule)) {
    const userName = pickUserName(awsContext, item);

    return [
      "Open AWS IAM.",
      "Go to Users.",
      `Search for and open ${userName}.`,
      "Open the Security credentials tab.",
      "Review Multi-factor authentication (MFA) and confirm no MFA device is currently assigned.",
      "Review Access keys and identify the active key tied to this finding.",
      "Confirm whether the user still requires console access, programmatic access, or both.",
      "Assign an approved MFA device if the user must keep console access.",
      "Rotate or deactivate the active access key if it is unnecessary, unowned, old, or not tied to an approved workload.",
      "Confirm the user has MFA enabled and any remaining active key has an owner, purpose, rotation plan, and least-privilege permissions.",
      "Save evidence of the MFA assignment, access-key review, and any key rotation or deactivation.",
      "Return to OASYS and run Full Cloud Assessment to verify the no-MFA-with-active-key condition is cleared.",
    ];
  }

  if (rule.includes("IAM_USER_MFA")) {
    return [
      "Open AWS IAM.",
      "Go to Users.",
      "Open the affected IAM user.",
      "Open the Security credentials tab.",
      "Find Multi-factor authentication (MFA).",
      "Choose Assign MFA device and complete the MFA setup.",
      "Confirm the user shows an active MFA device.",
      "Return to OASYS and run Full Cloud Assessment to verify IAM user MFA is detected.",
    ];
  }

  if (rule.includes("ACCESS_KEY") && rule.includes("OLD")) {
    const userName = pickUserName(awsContext, item);
    const slotLabel = getAccessKeySlotLabel(awsContext?.accessKeySlot, rule).toLowerCase();

    return [
      "Open AWS IAM.",
      "Go to Users.",
      `Search for and open ${userName}.`,
      "Open the Security credentials tab.",
      `Locate ${slotLabel}.`,
      "Review last-used date, service, and region before changing the key.",
      "Identify the dependent workload, secret store, automation, or credentials file using this key.",
      "Create a replacement key only if the workload still requires programmatic access.",
      "Update the dependent workload or secret store to use the replacement key.",
      "Test the workload with the replacement key.",
      "Deactivate the old key and monitor for failed authentication.",
      "Delete the old key after validation, then run Full Cloud Assessment.",
    ];
  }

  if (rule.includes("ACCESS_KEY") && rule.includes("NEVER_USED")) {
    const userName = pickUserName(awsContext, item);
    const slotLabel = getAccessKeySlotLabel(awsContext?.accessKeySlot, rule).toLowerCase();

    return [
      "Open AWS IAM.",
      "Go to Users.",
      `Search for and open ${userName}.`,
      "Open the Security credentials tab.",
      `Locate ${slotLabel} marked as never used.`,
      "Confirm no workload owner needs the key.",
      "Deactivate the key.",
      "Delete the key after confirming there is no production impact.",
      "Run Full Cloud Assessment to verify the unused key is no longer reported.",
    ];
  }

  if (rule.includes("ACCESS_KEY") && rule.includes("ACTIVE")) {
    const userName = pickUserName(awsContext, item);
    const slotLabel = getAccessKeySlotLabel(awsContext?.accessKeySlot, rule).toLowerCase();

    return [
      "Open AWS IAM.",
      "Go to Users.",
      `Search for and open ${userName}.`,
      "Open the Security credentials tab.",
      `Review ${slotLabel}.`,
      "Confirm the key has an owner, business purpose, and rotation plan.",
      "Deactivate unnecessary keys and rotate keys that are still required.",
      "Delete retired keys after validation.",
      "Run Full Cloud Assessment to verify key status is current.",
    ];
  }

  if (rule.includes("PASSWORD_UNUSED") || rule.includes("USER_UNUSED")) {
    return [
      "Open AWS IAM.",
      "Go to Users.",
      "Open the affected user.",
      "Review Console last sign-in and access key last used details.",
      "Confirm whether the user still needs access.",
      "Remove console access, deactivate keys, or delete the user if no longer required.",
      "Document any exception for retained access.",
      "Run Full Cloud Assessment to verify the stale identity condition is cleared.",
    ];
  }

  if (rule.includes("ADMIN_POLICY") || rule.includes("WILDCARD")) {
    return [
      "Open AWS IAM.",
      "Open the affected user, role, group, or customer managed policy.",
      "Open the Permissions or Policy versions view.",
      "Review the policy JSON for AdministratorAccess, Action '*', Resource '*', or broad service wildcards.",
      "Replace broad access with least-privilege actions and resources.",
      "Attach the reduced policy and remove the excessive policy version or attachment.",
      "Document approved exceptions in OASYS.",
      "Run Full Cloud Assessment to confirm the permission exposure is resolved.",
    ];
  }

  return null;
}

export function buildIamValidationText(item = {}) {
  const rule = String(item?.rule_id || item?.ruleId || "").toUpperCase();

  if (
    rule.includes("ROLE_ASSUME_EXTERNAL_ACCOUNT") ||
    rule.includes("IAM_ROLE_TRUST_POLICY_EXTERNAL_ACCOUNT") ||
    rule.includes("TRUST_POLICY_EXTERNAL_ACCOUNT")
  ) {
    return "OASYS will mark this improved after the next sync detects the IAM role trust policy no longer allows unapproved external account principals, or detects that the approved external trust is tightly restricted and documented.";
  }

  if (isUnusedRoleRule(rule)) {
    return "OASYS will mark this improved after the next sync detects the unused role has been deleted, recently used with an approved business purpose, or documented with an authorized exception.";
  }

  if (isUserMfaAndActiveKeyRule(rule)) {
    return "OASYS will mark this improved after the next IAM inventory sync detects the affected user has MFA enabled and no unmanaged active access key condition remains.";
  }

  if (rule.includes("MFA")) {
    return "OASYS will mark this improved after the next sync detects MFA as enabled for the affected identity.";
  }

  if (rule.includes("ACCESS_KEY")) {
    return "OASYS will mark this improved after the next sync detects the old, unused, or unnecessary key is removed, rotated, or inactive.";
  }

  if (rule.includes("ADMIN_POLICY") || rule.includes("WILDCARD")) {
    return "OASYS will mark this improved after the next sync detects that broad admin or wildcard permissions have been removed or formally excepted.";
  }

  return null;
}